import styles from "../../assets/styles/Transaction/Transaction.module.scss";
import {
  AppPage,
  MimaButton,
  MimaNewFilter,
  MimaTable,
  MimaText,
  PageLoader,
} from "../../components";
import { BusinessInsightComponent as BIC } from "../../components";
import React, { useMemo, useState } from "react";
import { amountFormatter } from "../../utils/utils";
import { transactionStore } from "../../stores";
import { AnimatePresence } from "framer-motion";
import TxMoreInfo from "./TxMoreInfo";
import constant from "../../utils/constant";
import MimaOptionMenu from "../../components/OptionMenu/";
import moment from "moment";
import { downloadXlsx2 } from "../../utils/download";
import { UpdateTransactionStatusContainer } from "../../containers";
import ReverseTransaction from "./ReverseTransaction";

const Transaction = ({
  loading,
  closeModal,
  filterHandler,
  onLimitChange,
  data,
  currentPage,
  setCurrentPage,
  limit,
  isLoading,
  isFetching,
  setSearchQuery,
  setCurrencyCode,
  currencyCode,
  viewMoreModal,
  goToViewMore,
  goToReverse,
  reverseModal,
  updateStatusModal,
  goToUpdateStatus,
  handleRetriggerPayment,
  refetch,
}) => {
  const [curr, setCurr] = useState("NGN");
  const transactionsAnalysis = transactionStore.transactionsAnalysis;
  const analysisPerCurr = transactionsAnalysis.filter((x) => {
    return x.currencyCode === curr;
  });

  const [singleTx, setSingleTx] = useState({});
  const [showFilter, setShowFilter] = useState(false);

  const filterByData = [
    { value: "Date", key: "createdAt" },
    { value: "Business", key: "business" },
    { value: "Status", key: "status" },
  ];

  const statusParams = [
    { value: "Paid", key: "PAID" },
    { value: "Pending", key: "PENDING" },
    { value: "Reversed", key: "REVERSED" },
    { value: "Failed", key: "FAILED" },
    { value: "Partially-Paid", key: "PARTIALLY-PAID" },
    { value: "Unpaid", key: "UNPAID" },
    { value: "Void", key: "VOID" },
  ];

  const tableData = useMemo(() => {
    if (data?.status === constant.Success) {
      return data?.data?.transactions || [];
    }
    return [];
  }, [data]);

  const tableColumns = [
    {
      Header: "Date",
      accessor: "paidDate",
      Cell: ({ cell }) => {
        const date = cell.row.original.paidDate;

        return <span>{moment(date).format("DD/MM/YYYY HH:mm a")}</span>;
      },
    },
    {
      Header: "Business Name",
      accessor: "tradingName",
      Cell: ({ cell }) => {
        const businesName = cell.row.original?.business?.tradingName;
        return <span>{businesName}</span>;
      },
    },
    {
      Header: "Narration",
      accessor: "narration",
      Cell: ({ cell }) => {
        const narration = cell.value;
        return (
          <span>
            {narration?.length > 30
              ? narration.substring(0, 30) + "..."
              : narration}
          </span>
        );
      },
    },
    {
      Header: "Amount Charged",
      accessor: "transactionAmount",
      Cell: ({ cell }) => {
        const currencyCode = cell.row.original.currencyCode;
        const amountCharged = cell.row.original.transactionAmount;
        const formattedAmountCharged =
          amountFormatter(currencyCode).format(amountCharged);

        return <span>{formattedAmountCharged}</span>;
      },
    },
    {
      Header: "Amount Paid",
      accessor: "amountPaid",
      Cell: ({ cell }) => {
        const currencyCode = cell.row.original.currencyCode;
        const amountPaid = cell.row.original.amountPaid;
        const formattedAmountpaid =
          amountFormatter(currencyCode).format(amountPaid);

        return <span>{formattedAmountpaid}</span>;
      },
    },
    {
      Header: "Type",
      accessor: "type",
      Cell: ({ cell }) => {
        const type = cell.value;
        return (
          <span className={type === "INFLOW" ? styles.income : styles.expense}>
            {type}
          </span>
        );
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ cell }) => {
        const status = cell.row.original.status;
        const styleClass = `status ${status === "FULLY-PAID" || status === "PAID" ? "status__green" : status === "PENDING" || status === "PARTIALLY-PAID" ? "status__dark__yellow" : status === "VOID" || status === "REVERSED" ? "status__grey" : "status__red"}`;
        return <span className={styleClass}>{status}</span>;
      },
    },

    {
      Header: "Actions",
      Cell: ({ cell }) => {
        const status = cell.row.original.status;
        const category = cell.row.original.category;
        const item = cell.row.original;
        return (
          <MimaOptionMenu
            options={[
              { label: "View More", value: "View More" },
              { label: "Reverse", value: "Reverse" },
              category === "WALLET" &&
              status === "PENDING" && {
                label: "Update Status",
                value: "Update Status",
              },
            ]}
            onOptionSelected={(option) => {
              setSingleTx(item);
              if (option.value === "Update Status") {
                goToUpdateStatus();
              } else if (option.value === "View More") {
                goToViewMore();
              }
              else if (option.value === "Reverse") {
                goToReverse();
              }
            }}
          />
        );
      },
    },
  ];

  const printData = tableData.map((item) => ({
    Date: item?.createdAt
      ? moment(item?.createdAt).format("DD-MM-YYYY HH:mm a")
      : "",
    "Business Name": item?.business?.tradingName || "",
    Narration: item?.narration || "",
    "Amount Charged":
      amountFormatter(item.currencyCode).format(item.transactionAmount) || "",
    "Amount Paid":
      amountFormatter(item.currencyCode).format(item.amountPaid) || "",
    "Transaction Type": item?.type || "",
    "Transaction Status": item?.status || "",
  }));

  return (
    <AppPage pageTitle="All Transactions">
      <>
        <div className={styles.filterClass}>
          <MimaButton
            title={showFilter ? "Close Filter" : "Filter"}
            onClick={() => setShowFilter(!showFilter)}
          />
        </div>
        <MimaNewFilter
          showFilter={showFilter}
          setShowFilter={setShowFilter}
          filterByData={filterByData}
          filterHandler={filterHandler}
          statusParams={statusParams}
        />
      </>
      {loading ? (
        <PageLoader title="while we load your information" loading={loading} />
      ) : (
        <>
          <div className={styles.main_nav}>
            {transactionsAnalysis?.map((item) => (
              <div
                key={item.currencyCode}
                className={
                  curr === item.currencyCode
                    ? styles.main_nav_item_active
                    : styles.main_nav_item
                }
                onClick={() => {
                  setCurr(item.currencyCode);
                  setCurrencyCode(item.currencyCode);
                }}
              >
                {item.currencyCode} Transactions
              </div>
            ))}
          </div>
          <>
            {transactionsAnalysis.length > 0 ? (
              <div className={styles.upper__metrics}>
                {analysisPerCurr.map((tranx) => (
                  <React.Fragment key={tranx._id}>
                    <BIC
                      info="Total sum of Revenue generated "
                      title="Total Income/Revenue"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalIncome
                      )}
                      styleClass="cards_green_1"
                    />
                    <BIC
                      info="Total sum of expenses incurred"
                      title="Total Expenses"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalExpenses
                      )}
                      styleClass="cards_red_2"
                    />
                    <BIC
                      info="Total sum of discounts given"
                      title="Total Discounts"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalDiscount
                      )}
                      styleClass="cards_blue_1"
                    />
                    <BIC
                      info="Total sum of expected revenue"
                      title="Outstanding Income"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalExpectedBalance
                      )}
                      styleClass="cards_green_3"
                    />
                    <BIC
                      info="Total sum of debt incurred"
                      title="Amount Owed"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalAmountOwed
                      )}
                      styleClass="cards_red_3"
                    />
                    <BIC
                      info="Total sum of Value added tax collected"
                      title="Total VAT"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalVAT
                      )}
                      styleClass="cards_blue_2"
                    />
                    <BIC
                      info="Total sum of money received "
                      title="Total Money In"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalNetCapital
                      )}
                      styleClass="cards_green_2"
                    />
                    <BIC
                      info="Total sum of money sent"
                      title="Total Money Out"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalMoneyOut
                      )}
                      styleClass="cards_red_1"
                    />
                    <BIC
                      info="Total sum of withdrwals made"
                      title="Total Withdrawals"
                      amount={amountFormatter(tranx.currencyCode).format(
                        tranx.totalWithdrawal
                      )}
                      styleClass="cards_yellow_1"
                    />
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <MimaText variant="subtitleBold" align="center">
                {" "}
                No Transaction analysis available
              </MimaText>
            )}
          </>

          <div className={styles.filterClass}>
            <MimaButton
              title={`Download ${currencyCode} Report`}
              width={27}
              onClick={() => {
                downloadXlsx2(printData);
              }}
            />
          </div>

          <MimaTable
            tableData={tableData}
            tableColumns={tableColumns}
            searchPlaceholder="Search Transaction"
            searchVariant="wide"
            totalItems={data?.data?.totalCounts}
            onLimitChange={onLimitChange}
            isLoading={isLoading}
            isFetching={isFetching}
            limit={limit}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            setSearchQuery={setSearchQuery}
          />
        </>
      )}

      {(viewMoreModal || updateStatusModal || reverseModal) && (
        <div className="modal">
          <AnimatePresence>
            {viewMoreModal && (
              <TxMoreInfo
                closeModal={closeModal}
                singleTx={singleTx}
                loading={loading}
                refetch={refetch}
              />
            )}
            {updateStatusModal && (
              <UpdateTransactionStatusContainer
                closeModal={closeModal}
                singleTx={singleTx}
              />
            )}
            {reverseModal &&  (
              <ReverseTransaction
                closeModal={closeModal}
                singleTx={singleTx}
                refetch={refetch}
                loading={loading}
              />
            )}
          </AnimatePresence>
        </div>
      )}
    </AppPage>
  );
};

export default Transaction;
